// This is the client's API it uses for interacting with the `server`.
import axios, { AxiosError } from "axios";

import { getPasswordErrorMessages } from "../helpers/password-errors";
import { PreclassCheckResultType } from "../shared/types/preclass-check";

import { ImportExternalSectionResponseType } from "./api-data-types";

import type {
  BulkImportType,
  ClassUserType,
  CsrfTokenType,
  FormattedValidationError,
  ImportSectionRequestType,
  InviteKeySectionType,
  OrgType,
  OutcomeSubgroupType,
  OutcomeType,
  PageAccessDataType,
  SectionInviteType,
  SectionType,
  UserInformationType,
  UserType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { PasswordErrorKey } from "i18n/i18next";
import { store } from "store/index";
import { getReadableAxiosError } from "utils/alerts";
import { getCsrfToken } from "utils/auth";
import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";

export const CSRF_TOKEN_HEADER = "X-CSRFToken";
export const SERVER_API_PATH = "/api/v1";

export const serverApi = axios.create({
  baseURL: window._env_?.SERVER_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});
serverApi.interceptors.request.use((config) => {
  const state = store.getState();
  // Note: we can't use the selector here because in some cases the
  // store may not be initialized yet.
  if (state?.view?.userIsInactive) {
    // Abort the request instead of throwing an error to prevent it from reaching rollbar
    const method = config.method?.toUpperCase() || "UNKNOWN";
    const url = config.url || "UNKNOWN";
    logInfoWithDebugFlag("debug_sockets", "Canceling request due to inactive user", {
      method,
      url,
    });
    const abortController = new AbortController();
    abortController.abort();
    // eslint-disable-next-line no-param-reassign
    config.signal = abortController.signal;
  }

  if (!config.headers[CSRF_TOKEN_HEADER]) {
    const csrfToken = getCsrfToken();
    // eslint-disable-next-line no-param-reassign
    config.headers[CSRF_TOKEN_HEADER] = csrfToken;
  }

  return config;
});

export const reportApiError = (name: string, error: unknown) => {
  if (error instanceof AxiosError) {
    const message = getReadableAxiosError(error);
    rollbarAndLogError(`${name} failed with message: ${message}`, error);
  } else {
    rollbarAndLogError(`${name}`, error);
  }
};

export function serverLogout() {
  return serverApi.post(`${SERVER_API_PATH}/auth/logout/`);
}

// All server API calls should have the `server` prefix to clarify that it's going to the `server`
// (as opposed to `collaboration`).

export interface ServerApiUserResponseType {
  user_id: string;
  collaboration_jwt: string;
  // See store/auth.ts for how this is used
  course_to_org_id_map: Record<string, string>;
}

export async function serverGetUser(): Promise<ServerApiUserResponseType> {
  // We don't catch/report an error here because this is a common call that will fail
  // if a user is just not logged in. Handled in detail further upstream.
  const response = await serverApi.get<ServerApiUserResponseType>(`${SERVER_API_PATH}/users/me/`, {
    withCredentials: true,
  });
  return response.data;
}

export type PasswordType = {
  current_password: string;
  password: string;
  confirm_password: string;
};

export type PasswordResetRequestType = {
  token: string;
  new_password: string;
  confirm_password: string;
};

export type RegistrationRequestType = {
  token: string;
  new_password: string;
  confirm_password: string;
  first_name: string;
  last_name?: string;
  eula_accepted: boolean;
};

export interface ServerApiDetailResponseType {
  detail: string;
}

export async function serverSetPassword(
  passwords: PasswordType,
): Promise<ServerApiDetailResponseType> {
  // We don't do any error handling here; it's handled in the calling function.

  const response = await serverApi.post<ServerApiDetailResponseType>(
    `${SERVER_API_PATH}/auth/users/set_password/`,
    {
      ...passwords,
    },
  );

  return response.data;
}

export async function serverRequestResetPassword(email: string) {
  try {
    const response = await serverApi.post<ServerApiUserResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/request_password_reset/`,
      {
        email,
      },
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const code = error.response?.status;

      let message: string;
      // TODO: Double check that the client is requiring email field before this request is sent.
      if (code === 400) {
        message = t("error.toasts.missing_field", { item: "Email" });
      } else {
        message = getReadableAxiosError(error);
      }

      rollbarAndLogError(`serverRequestResetPassword failed with message: ${message}`, error);

      throw new Error(message);
    } else {
      rollbarAndLogError("serverRequestResetPassword", error);
    }

    throw error;
  }
}

export async function serverResetPassword(
  token: string,
  newPassword: string,
  confirmPassword: string,
) {
  try {
    const requestData: PasswordResetRequestType = {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    };

    const response = await serverApi.post<ServerApiUserResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/reset_password/`,
      requestData,
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const code = error.response?.status;

      let message = "";
      if (code === 400) {
        const data = error.response?.data as FormattedValidationError<PasswordErrorKey>;

        if (!data) {
          rollbarAndLogError("serverResetPassword", error);
          return;
        }

        for (const { details, t: translationKeys } of Object.values(data)) {
          message += getPasswordErrorMessages(translationKeys, details);
        }

        if (!message) {
          message = t("error.error");
        }
      } else if (code === 403) {
        message = t("error.toasts.token_invalid");
      } else {
        message = getReadableAxiosError(error);
      }

      rollbarAndLogError(`serverResetPassword failed with message: ${message}`, error);

      throw new Error(message);
    } else {
      rollbarAndLogError("serverResetPassword", error);
    }

    throw error;
  }
}

export async function serverRegisterUser(
  token: string,
  newPassword: string,
  confirmPassword: string,
  firstName: string,
  lastName: string,
  eulaAccepted: boolean,
) {
  try {
    const requestData: RegistrationRequestType = {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
      first_name: firstName,
      last_name: lastName,
      eula_accepted: eulaAccepted,
    };

    const response = await serverApi.post<ServerApiUserResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/register/`,
      requestData,
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const code = error.response?.status;

      let message = "";
      if (code === 400) {
        const data = error.response?.data as FormattedValidationError<PasswordErrorKey>;

        if (!data) {
          rollbarAndLogError("serverRegisterUser", error);
          return;
        }

        for (const { details, t: translationKeys } of Object.values(data)) {
          message += getPasswordErrorMessages(translationKeys, details);
        }

        if (!message) {
          message = getReadableAxiosError(error);
        }
      } else if (code === 403) {
        message = t("error.toasts.token_invalid");
      } else {
        message = getReadableAxiosError(error);
      }

      rollbarAndLogError(`serverRegisterUser failed with message: ${message}`, error);

      throw new Error(message);
    } else {
      rollbarAndLogError("serverRegisterUser", error);
    }

    throw error;
  }
}

export interface OnboardingDataResponseType {
  email: string;
  first_name: string;
  last_name: string;
  org_name: string;
  pending_system_check: boolean;
}

export async function getOnboardingData(
  token: string,
  orgId: string,
): Promise<OnboardingDataResponseType> {
  try {
    const response = await serverApi.get<OnboardingDataResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/get_onboarding_data/`,
      {
        params: { token, org_id: orgId },
      },
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const code = error.response?.status;

      let message = "";
      if (code === 422) {
        // User has already completed onboarding
        throw error;
      } else if (code === 400) {
        message = t("error.toasts.missing_field", { item: "Token or Organization ID" });
      } else if (code === 403) {
        message = error.response?.data?.detail || t("user_options_menu.redeem.unknown_error");
      } else {
        message = getReadableAxiosError(error);
      }

      rollbarAndLogError(`getOnboardingData failed with message: ${message}`, error);
      throw new Error(message);
    } else {
      rollbarAndLogError("getOnboardingData", error);
    }

    throw error;
  }
}

export async function switchOrg(newOrgId: string) {
  try {
    return await serverApi.post<OrgType>(`${SERVER_API_PATH}/orgs/orgs/${newOrgId}/switch/`);
  } catch (error) {
    reportApiError("switchOrg", error);
    throw error;
  }
}

export type RegistrationType = {
  token: string;
  org_id: string;
};

export interface EulaError extends Error {
  name: "EULA_NOT_ACCEPTED";
  token?: string;
  orgId?: string;
}

export async function serverLogin(email: string, password: string) {
  try {
    const response = await serverApi.get<CsrfTokenType>(`${SERVER_API_PATH}/auth/csrf/`);
    const csrftoken = response.data.csrftoken;
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    const headers = {
      "Content-Type": "application/x-www-form-urlencoded",
      [CSRF_TOKEN_HEADER]: csrftoken,
    };

    await serverApi.post(`${SERVER_API_PATH}/auth/login/`, formData, {
      headers,
    });
  } catch (err) {
    // Handle 422 case (EULA not accepted, redirect to onboarding)
    const error = err as AxiosError<RegistrationType>;
    if (error?.response?.status === 422) {
      const responseData = error.response.data;
      const eulaError = new Error("EULA_NOT_ACCEPTED") as EulaError;

      eulaError.name = "EULA_NOT_ACCEPTED";
      eulaError.token = responseData.token;
      eulaError.orgId = responseData.org_id;

      throw eulaError;
    }

    // Don't bother reporting login failures due to Unauthorized (likely just wrong credentials provided by user).
    if (error.response?.status !== 401) {
      reportApiError("serverLogin", error);
    }

    throw error;
  }
}

export async function serverCreateImpersonationUrl(targetUserId: string): Promise<string> {
  try {
    const response = await serverApi.post<{
      redemption_url: string;
    }>(`${SERVER_API_PATH}/auth/impersonation_session/`, { target_user_id: targetUserId });
    return response.data.redemption_url;
  } catch (error) {
    reportApiError("serverCreateImpersonationUrl", error);
    throw error;
  }
}

export async function serverGetCourseInvite(courseId: string): Promise<SectionInviteType[]> {
  try {
    const response = await serverApi.get<SectionInviteType[]>(
      `${SERVER_API_PATH}/auth/section_invite/?section_id=${courseId}`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetCourseInvite", error);
    throw error;
  }
}

export async function serverCreateCourseInvite(courseId: string): Promise<SectionInviteType> {
  try {
    const response = await serverApi.post<SectionInviteType>(
      `${SERVER_API_PATH}/auth/section_invite/`,
      { section_id: courseId },
    );
    return response.data;
  } catch (error) {
    reportApiError("serverCreateCourseInvite", error);
    throw error;
  }
}

export async function serverDeleteCourseInvite(inviteId: string): Promise<void> {
  try {
    await serverApi.delete(`${SERVER_API_PATH}/auth/section_invite/${inviteId}/`);
  } catch (error) {
    reportApiError("serverDeleteCourseInvite", error);
    throw error;
  }
}

export async function serverRedeemInviteKey(inviteKey: string) {
  try {
    const ret = await serverApi.post(
      `${SERVER_API_PATH}/auth/section_invite/redeem_for_existing_user/?invite_key=${inviteKey}`,
    );
    return ret.data as { section_id: string; user_id: string };
  } catch (error) {
    reportApiError("serverRedeemInviteKey", error);
    throw error;
  }
}

export async function serverRegisterUserWithInviteKey(
  inviteKey: string,
  userData: UserInformationType & {
    password: string;
    password_confirm: string;
    eula_accepted: boolean;
  },
) {
  try {
    const ret = await serverApi.post(
      `${SERVER_API_PATH}/auth/section_invite/redeem_for_new_user/?invite_key=${inviteKey}`,
      userData,
    );
    return ret.data as UserType;
  } catch (error) {
    reportApiError("serverRegisterUserWithInviteKey", error);
    throw error;
  }
}

export async function serverGetCourseMetadataFromInviteKey(inviteKey: string) {
  try {
    const ret = await serverApi.get(
      `${SERVER_API_PATH}/auth/section_invite/get_course_metadata/?invite_key=${inviteKey}`,
    );
    return ret.data as InviteKeySectionType;
  } catch (error) {
    reportApiError("serverGetCourseMetadataFromInviteKey", error);
    throw error;
  }
}

export async function serverExportCourse(courseId: string): Promise<JSON> {
  try {
    const results = await serverApi.post<JSON>(
      `${SERVER_API_PATH}/material/sections/${courseId}/export_external/`,
    );

    return results.data;
  } catch (error) {
    reportApiError("serverExportCourse", error);
    throw error;
  }
}

export async function serverImportExternalCourse(
  formData: FormData,
): Promise<ImportExternalSectionResponseType> {
  try {
    const results = await serverApi.post<ImportExternalSectionResponseType>(
      `${SERVER_API_PATH}/material/sections/import_external/`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return results.data;
  } catch (error) {
    reportApiError("serverImportExternalCourse", error);
    throw error;
  }
}

export async function serverCloneCourse(
  course_id: string,
  course_code_id?: string,
  term_id?: string,
): Promise<void> {
  const data: { [key: string]: string } = {};
  data.target_course_code_id = course_code_id;
  data.target_term_id = term_id;

  try {
    await serverApi.post(`${SERVER_API_PATH}/material/sections/${course_id}/clone/`, data);
  } catch (error) {
    reportApiError("serverCloneCourse", error);
    throw error;
  }
}

export async function serverImportCourse(data: ImportSectionRequestType): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/sections/import/`, data);
  } catch (error) {
    reportApiError("serverImportCourse", error);
    throw error;
  }
}

export async function serverUpsertPageAccessData(
  data: Omit<PageAccessDataType, "id" | "completed_at"> & { completed_at?: string | null },
): Promise<PageAccessDataType | undefined> {
  try {
    const response = await serverApi.post<PageAccessDataType>(
      `${SERVER_API_PATH}/material/page_access_data/upsert/`,
      data,
    );
    const item = response.data as PageAccessDataType & { section_version?: number };

    // Match createPubsubSlice behavior: store the model, but drop section_version.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { section_version, ...pageAccessData } = item;
    store.dispatch({ type: "page_access_data/updateOne", payload: pageAccessData });

    return pageAccessData;
  } catch (error) {
    reportApiError("serverUpsertPageAccessData", error);
    return undefined;
  }
}

export async function serverUnlinkCourse(
  courseId: string,
  deletePagesAndAssessments: boolean,
): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/sections/${courseId}/unlink/`, {
      delete_pages_and_assessments: deletePagesAndAssessments,
    });
  } catch (error) {
    reportApiError("serverUnlinkCourse", error);
    throw error;
  }
}

export async function serverCopyTopic(topicId: string, courseIds: string[]): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/topics/${topicId}/copy/`, {
      course_ids: courseIds,
    });
  } catch (error) {
    reportApiError("serverCopyTopic", error);
    throw error;
  }
}

export async function serverAddCourseCodesToGrouping(
  groupingId: string,
  courseCodeIds: string[],
): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/material/course_groupings/${groupingId}/add_courses/`,
      { course_code_ids: courseCodeIds },
    );
  } catch (error) {
    reportApiError("serverAddCourseCodesToGrouping", error);
    throw error;
  }
}

export async function serverMoveActivityWithinCourse(
  activityId: string,
  topicId: string,
): Promise<void> {
  const data = { topic_id: topicId };

  try {
    await serverApi.post(`${SERVER_API_PATH}/material/activities/${activityId}/move/`, data);
  } catch (error) {
    reportApiError("serverMoveActivityWithinCourse", error);
    throw error;
  }
}

export async function serverCopyActivity(activityId: string): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/activities/${activityId}/copy/`);
  } catch (error) {
    reportApiError("serverCopyActivity", error);
    throw error;
  }
}

export async function serverMovePageWithinCourse(
  pageId: string,
  category: string,
  topicId?: string,
): Promise<void> {
  const data: { category: string; topic_id?: string } = { category };
  if (topicId) {
    data.topic_id = topicId;
  }

  try {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/move/`, data);
  } catch (error) {
    reportApiError("serverMovePageWithinCourse", error);
    throw error;
  }
}

export async function serverCopyPage(
  pageId: string,
  courseIds?: string[],
  activityIds?: string[],
): Promise<void> {
  const data: { course_ids?: string[]; activity_ids?: string[] } = {};
  if (courseIds?.length > 0) {
    data.course_ids = courseIds;
  }

  if (activityIds?.length > 0) {
    data.activity_ids = activityIds;
  }

  try {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/copy/`, data);
  } catch (error) {
    reportApiError("serverCopyPage", error);
    throw error;
  }
}

export async function serverGetLicodeToken(
  username: string,
  role: string,
  roomName: string,
  licodeUrl: string,
): Promise<string> {
  try {
    const data = {
      username,
      role,
      room_name: roomName,
      licode_nuve_server: licodeUrl,
    };
    const results = await serverApi.post<string>(`${SERVER_API_PATH}/av/licodetoken/`, data);
    return results.data;
  } catch (error) {
    reportApiError("serverGetLicodeToken", error);
    throw error;
  }
}

export async function serverGetOpentokToken(classSessionId: string) {
  try {
    const data = {
      class_session_id: classSessionId,
    };
    const results = await serverApi.post<string>(`${SERVER_API_PATH}/av/opentoktoken/`, data);
    logInfoWithDebugFlag("debug_virtual_class", "[serverGetOpentokToken] results", results.data);
    const resultData = results.data as unknown as {
      "api-key": string;
      token: string;
    };
    return {
      token: resultData.token,
      apiKey: resultData["api-key"],
      opentokSessionId: resultData["opentok-session-id"],
    };
  } catch (error) {
    reportApiError("serverGetOpentokToken", error);
    throw error;
  }
}

export async function serverGetDailyToken(userId: string, roomName: string) {
  try {
    const data = {
      user_id: userId,
      room_name: roomName,
    };
    const results = await serverApi.post<string>(`${SERVER_API_PATH}/av/dailytoken/`, data);
    if (results.status !== 200) {
      throw new Error("Failed to get daily token");
    }
    const resultData = results.data as unknown as {
      token: string;
    };
    return {
      token: resultData.token,
      apiKey: "dailyApiKey",
    };
  } catch (error) {
    reportApiError("serverGetDailyToken", error);
    throw error;
  }
}

export async function serverDistributeUsersToGroups(
  pageId: string,
  courseUserIds: string[] | null,
): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/distribute/`, {
      section_user_ids: courseUserIds,
    });
  } catch (error) {
    reportApiError("serverDistributeUsersToGroups", error);
    throw error;
  }
}

export async function serverReleaseAssessments(pageId: string): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/release_assessments/`);
  } catch (error) {
    reportApiError("serverReleaseAssessments failed with error", error);
    throw error;
  }
}

export async function serverBulkCreateSubmissions(page_id: string): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/submissions/bulk/`, { page_id });
  } catch (error) {
    reportApiError("serverBulkCreateSubmissions", error);
    throw error;
  }
}

export async function serverRaiseHand(classUserId: string): Promise<ClassUserType> {
  try {
    const response = await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/raise-hand/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverRaiseHand", error);
    throw error;
  }
}

export async function serverLowerHand(classUserId: string): Promise<ClassUserType> {
  try {
    const response = await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/lower-hand/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverLowerHand", error);
    throw error;
  }
}

export async function serverClearVotes(classSessionId: string): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/clear-votes/`,
    );
  } catch (error) {
    reportApiError("serverClearVotes", error);
    throw error;
  }
}

export async function serverEndSession(classSessionId: string): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/end-session/`,
    );
  } catch (error) {
    reportApiError("serverEndSession", error);
    throw error;
  }
}

export async function serverMoveOutcome(
  outcomeId: string,
  destination: { group_id: string } | { subgroup_id: string },
): Promise<OutcomeType> {
  try {
    const response = await serverApi.post<OutcomeType>(
      `${SERVER_API_PATH}/material/outcomes/${outcomeId}/move/`,
      destination,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverMoveOutcome", error);
    throw error;
  }
}

export async function serverMoveOutcomeSubgroup(
  outcomeSubgroupId: string,
  groupId: string,
): Promise<OutcomeSubgroupType> {
  try {
    const response = await serverApi.post<OutcomeSubgroupType>(
      `${SERVER_API_PATH}/material/outcome_subgroups/${outcomeSubgroupId}/move/`,
      { group_id: groupId },
    );
    return response.data;
  } catch (error) {
    reportApiError("serverMoveOutcomeSubgroup", error);
    throw error;
  }
}

// New streamlined bulk import endpoints
export async function serverGetBulkImportSignedUrl(
  importType: BulkImportType,
  filename: string,
): Promise<{ upload_url: string; s3_key: string }> {
  try {
    const results = await serverApi.post<{ upload_url: string; s3_key: string }>(
      `${SERVER_API_PATH}/material/bulk_import_jobs/get-signed-url/`,
      {
        import_type: importType,
        filename,
      },
    );

    return results.data;
  } catch (error) {
    reportApiError("serverGetBulkImportSignedUrl", error);
    throw error;
  }
}

export async function serverStartBulkImportJob(
  importType: BulkImportType,
  s3Key: string,
  originalFilename: string,
  forceSystemCheck = false,
): Promise<{ message: string; import_job_id: string }> {
  try {
    const results = await serverApi.post<{ message: string; import_job_id: string }>(
      `${SERVER_API_PATH}/material/bulk_import_jobs/start-import/`,
      {
        import_type: importType,
        s3_key: s3Key,
        original_filename: originalFilename,
        require_system_check: forceSystemCheck,
      },
    );

    return results.data;
  } catch (error) {
    reportApiError("serverStartBulkImportJob", error);
    throw error;
  }
}

export async function serverUpdateScreensharing(
  classSessionId: string,
  screensharingClassUserId: string | null,
  breakoutGroupId?: string | null,
): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/class_sessions/update-screensharing/`, {
      class_session_id: classSessionId,
      screensharing_class_user_id: screensharingClassUserId,
      breakout_group_id: breakoutGroupId,
    });
  } catch (error) {
    reportApiError("serverUpdateScreensharing", error);
    throw error;
  }
}

export async function serverCreateBreakouts(
  classSessionId: string,
  breakoutGroupType: "random" | "page",
  breakoutDuration: number,
  groupPageId: string | null,
  groupCount?: number,
  shouldSyncBreakoutsWithPageGroups?: boolean,
): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/material/class_sessions/create-breakouts/`, {
      class_session_id: classSessionId,
      breakout_group_type: breakoutGroupType,
      breakout_duration: breakoutDuration,
      group_page_id: groupPageId,
      group_count: groupCount,
      should_sync_breakouts_with_page_groups: shouldSyncBreakoutsWithPageGroups,
    });
  } catch (error) {
    reportApiError("serverCreateBreakoutGroups", error);
    throw error;
  }
}

export async function serverEndBreakouts(classSessionId: string): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/end-breakouts/`,
    );
  } catch (error) {
    reportApiError("serverEndBreakouts", error);
    throw error;
  }
}

export async function serverExtendBreakoutTime(
  classSessionId: string,
  add_duration: number,
): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/extend-breakout-time/`,
      { add_duration },
    );
  } catch (error) {
    reportApiError("serverExtendBreakoutTime", error);
    throw error;
  }
}

export async function serverUpdateClassUserBreakoutGroup(
  classUserId: string,
  breakoutGroupId,
): Promise<void> {
  try {
    await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/update-breakout-group/`,
      {
        breakout_group_id: breakoutGroupId,
      },
    );
  } catch (error) {
    reportApiError("serverUpdateClassUserBreakoutGroup", error);
    throw error;
  }
}

export async function serverListenToBreakoutGroup(
  classUserId: string,
  listenedBreakoutGroupId: string | null,
): Promise<void> {
  try {
    await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/listen-to-breakout-group/`,
      {
        listened_breakout_group_id: listenedBreakoutGroupId,
      },
    );
  } catch (error) {
    reportApiError("serverListenToBreakoutGroup", error);
    throw error;
  }
}

export type CreateNewUserRequestType = {
  email: string;
  first_name: string;
  last_name?: string;
  password?: string;
  confirm_password?: string;
  send_onboarding_email: boolean;
};

export async function serverCreateNewUser(
  userData: CreateNewUserRequestType,
): Promise<{ id: string }> {
  try {
    const response = await serverApi.post<{ id: string }>(
      `${SERVER_API_PATH}/orgs/org_users/create_new_user/`,
      userData,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverCreateNewUser", error);
    throw error;
  }
}

export async function serverStartRecordingSession(courseId: string): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/recording/recording_sessions/start_recording/`, {
      section_id: courseId,
    });
  } catch (error) {
    reportApiError("serverStartRecordingSession", error);
    throw error;
  }
}

export async function serverMarkRecordingSessionStarted(recordingSessionId: string): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/mark_started/`,
    );
  } catch (error) {
    reportApiError("serverMarkRecordingSessionStarted", error);
    throw error;
  }
}

// NOTE: serverMarkRecordingSessionStarted is never called from within the monarch application, but it is called by
// the recording agent (see https://github.com/minervaproject/recording-agent/blob/development/worker/interpreter.js),
// which is why we expose it as a global on the window object here. See also the interface declaration for this in types.ts.
window.serverMarkRecordingSessionStarted = serverMarkRecordingSessionStarted;

export async function serverStopRecordingSession(recordingSessionId: string): Promise<void> {
  try {
    await serverApi.post(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/stop_recording/`,
    );
  } catch (error) {
    reportApiError("serverStopRecordingSession", error);
    throw error;
  }
}

export async function serverGenerateRecordingUrl(
  recordingSessionId: string,
  setup: boolean,
): Promise<string> {
  try {
    const action = setup ? "generate_setup_url" : "generate_recording_url";
    const response = await serverApi.get<string>(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/${action}/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGenerateRecordingUrl", error);
    throw error;
  }
}

// In general, we should use storeApi for updates (including partial updates) and NOT this method.
// This should only be used from within components/monitoring/CoursesMonitoringTable.tsx
export async function serverUpdateCourseForMonitoring(
  courseId: string,
  courseData: Partial<SectionType>,
): Promise<SectionType> {
  try {
    const response = await serverApi.patch<SectionType>(
      `${SERVER_API_PATH}/material/sections/${courseId}/`,
      courseData,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverUpdateCourse", error);
    throw error;
  }
}

export async function serverGetBugReportSignedUrl(
  filename: string,
  contentType: string,
): Promise<{ upload_url: string; s3_key: string; file_url: string }> {
  try {
    const response = await serverApi.post(`${SERVER_API_PATH}/bug_report/reports/get-signed-url/`, {
      filename,
      content_type: contentType,
    });
    return response.data;
  } catch (error) {
    reportApiError("serverGetBugReportSignedUrl", error);
    throw error;
  }
}

export async function serverSubmitBugReport(data: {
  subject: string;
  description: string;
  section_id?: string | null;
  class_session_id?: string | null;
  submission_url?: string | null;
  user_uploaded_files_urls?: string[] | null;
}): Promise<void> {
  try {
    await serverApi.post(`${SERVER_API_PATH}/bug_report/reports/`, data);
  } catch (error) {
    reportApiError("serverSubmitBugReport", error);
    throw error;
  }
}

export async function serverSubmitSystemCheckResult(data: {
  check_end_result: "passed" | "failed";
  test_results: PreclassCheckResultType;
  browser: string | null;
  os: string | null;
}) {
  try {
    await serverApi.post(`${SERVER_API_PATH}/system_check/check_results/`, data);
  } catch (error) {
    reportApiError("serverSubmitCheckResult", error);
    throw error;
  }
}
